import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminSubpage from '../components/AdminSubpage';
import { adminCurrenciesAPI } from '../services/api';

const emptyForm = {
  code: '',
  name: '',
  symbol: '',
  rate_to_aed: '1',
  sort_order: '0',
  is_active: true,
};

const CurrencyFormPage = () => {
  const { currencyId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(currencyId);
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);

  useEffect(() => {
    if (!isEdit) {
      setFormData(emptyForm);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await adminCurrenciesAPI.getAll();
        const rows = response.data?.currencies || [];
        const c = rows.find((x) => String(x.id) === String(currencyId));
        if (!cancelled) {
          if (c) {
            setFormData({
              code: c.code || '',
              name: c.name || '',
              symbol: c.symbol || '',
              rate_to_aed: String(c.rate_to_aed ?? '1'),
              sort_order: String(c.sort_order ?? '0'),
              is_active: Boolean(c.is_active),
            });
          } else {
            toast.error('Currency not found');
          }
        }
      } catch (error) {
        if (!cancelled) toast.error(error.response?.data?.message || error.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currencyId, isEdit]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        code: formData.code.toUpperCase(),
        rate_to_aed: parseFloat(formData.rate_to_aed),
        sort_order: parseInt(formData.sort_order, 10) || 0,
      };
      if (isEdit) {
        await adminCurrenciesAPI.update(currencyId, payload);
        toast.success('Currency updated');
      } else {
        await adminCurrenciesAPI.create(payload);
        toast.success('Currency created');
      }
      navigate('/currencies');
    } catch (error) {
      toast.error('Error saving currency: ' + (error.response?.data?.message || error.message));
    }
  };

  const title = isEdit ? 'Edit currency' : 'Add currency';

  return (
    <AdminSubpage title={title} backTo="/currencies">
      {loading ? (
        <div className="loading">Loading…</div>
      ) : (
        <div className="admin-page-panel modal-content">
          <form onSubmit={submit}>
            <div className="form-group">
              <label>Code *</label>
              <input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                maxLength={3}
                required
                disabled={isEdit}
              />
            </div>
            <div className="form-group">
              <label>Name *</label>
              <input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Symbol</label>
              <input
                value={formData.symbol}
                onChange={(e) => setFormData({ ...formData, symbol: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Rate to AED *</label>
              <input
                type="number"
                step="0.000001"
                min="0.000001"
                value={formData.rate_to_aed}
                onChange={(e) => setFormData({ ...formData, rate_to_aed: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Sort Order</label>
              <input
                type="number"
                min="0"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />{' '}
                Active
              </label>
            </div>
            <div className="form-actions">
              <button className="btn btn-primary" type="submit">
                {isEdit ? 'Update' : 'Create'}
              </button>
              <button className="btn btn-secondary" type="button" onClick={() => navigate('/currencies')}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </AdminSubpage>
  );
};

export default CurrencyFormPage;
